import { loadReportOneDSEvent } from '@ms/yammer-telemetry-1ds';
import {
  bufferPreConfigEvent,
  getTelemetryClientConfig,
  isTelemetryClientConfigConfigured,
} from '@ms/yammer-telemetry-store';

import type { AnalyticsEvent, LogEvent, TelemetryEvent } from '../events';
import { isAnalyticsEvent, isAnalyticsV2Event, isLogEvent } from '../events';
import { applyMiddleware } from '../middleware';

import { reportAnalyticsEvent, reportAnalyticsEventNow } from './reportAnalytics';
import { reportAnalyticsV2Event } from './reportAnalyticsV2';
import { reportLogEvent, reportLogEventNow } from './reportLog';

type OneDSEventPayload = Parameters<Awaited<ReturnType<typeof loadReportOneDSEvent>>['reportOneDSEvent']>[0];

export const isOneDSDestination = (): boolean => {
  const config = getTelemetryClientConfig();

  return !!config?.oneDSInstrumentationKey;
};

export type Send1DSEvent = (payload: OneDSEventPayload) => void;
export const send1DSEvent: Send1DSEvent = (payload) => {
  void sendTo1DS(payload);
};

const sendTo1DS = async (payload: OneDSEventPayload): Promise<void> => {
  const { reportOneDSEvent } = await loadReportOneDSEvent();
  reportOneDSEvent(payload);
};

export type EmitToLegacy = (event: TelemetryEvent) => Promise<void>;
export const emitToLegacy: EmitToLegacy = async (event) => {
  if (!isTelemetryClientConfigConfigured()) {
    bufferPreConfigEvent(event);

    return;
  }

  const processedEvent = await applyMiddleware(event);

  if (isAnalyticsEvent(processedEvent)) {
    reportAnalyticsEvent(processedEvent);
  } else if (isAnalyticsV2Event(processedEvent)) {
    reportAnalyticsV2Event(processedEvent);
  } else {
    reportLogEvent(processedEvent);
  }
};

export type EmitToLegacyNow = (event: AnalyticsEvent | LogEvent) => Promise<void>;
export const emitToLegacyNow: EmitToLegacyNow = async (event) => {
  if (!isTelemetryClientConfigConfigured()) {
    bufferPreConfigEvent(event);

    return;
  }

  const processedEvent = await applyMiddleware(event);

  if (isAnalyticsEvent(processedEvent)) {
    await reportAnalyticsEventNow(processedEvent);
  } else if (isLogEvent(processedEvent)) {
    await reportLogEventNow(processedEvent);
  }
};

export type EmitEvent = (event: TelemetryEvent, oneDSPayload?: OneDSEventPayload) => void;
export const emitEvent: EmitEvent = (event, oneDSPayload) => {
  if (isOneDSDestination() && oneDSPayload) {
    void sendTo1DS(oneDSPayload);

    return;
  }

  void emitToLegacy(event);
};

export type EmitEventNow = (event: AnalyticsEvent | LogEvent, oneDSPayload?: OneDSEventPayload) => Promise<void>;
export const emitEventNow: EmitEventNow = async (event, oneDSPayload) => {
  if (isOneDSDestination() && oneDSPayload) {
    await sendTo1DS(oneDSPayload);

    return;
  }

  await emitToLegacyNow(event);
};

export const buildAnalyticsOneDSPayload = (event: AnalyticsEvent): OneDSEventPayload => ({
  baseType: event.type,
  name: event.name,
  data: event.properties,
});

export const buildAnalyticsV2OneDSPayload = (
  typeName: string,
  plainMessage: OneDSEventPayload['data']
): OneDSEventPayload => ({
  baseType: 'AnalyticsV2',
  name: typeName,
  data: plainMessage,
});
